import React, { useState } from 'react';
import { AutoComplete, SparkIcon, TooltipTrigger } from '@spark';
import { SUPPORTED_LANGUAGES, getLanguageNameByCode } from '@/shared/constants/languageConstants';
import '@/features/datasets/components/sparkSelectFullWidth.css';
import './LanguageSelector.css';

/**
 * Reusable LanguageSelector component (Spark AutoComplete multi-select).
 * Allows users to select one or more languages for a task.
 *
 * @param {Object} props
 * @param {string[]} props.value - Array of selected language codes (e.g., ["en", "es"])
 * @param {Function} props.onChange - Callback when selection changes, receives array of language codes
 * @param {string} props.label - Label text for the selector
 * @param {boolean} props.showTooltip - Whether to show the info tooltip
 * @param {string} props.tooltipText - Custom tooltip text
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.style - Additional styles
 * @param {boolean} props.hasError - Whether to show error state
 * @param {string} props.errorMessage - Error message shown below the selector when hasError is true
 */
const LanguageSelector = ({
    value = [],
    onChange,
    label = 'Task languages to support',
    showTooltip = true,
    tooltipText = 'Choose the languages that matter for your task. The model will tailor its training to them.',
    required = true,
    placeholder = 'Select languages',
    style = {},
    hasError = false,
    errorMessage = 'Please select at least one language',
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    // Open/close is delegated to AutoComplete via onOpenChange: its options panel
    // is portaled to document.body, so a manual outside-click check against a local
    // wrapper ref would close the panel before an option click can register.
    const handleOpenChange = (next) => {
        setOpen(next);
        if (!next) setQuery('');
    };

    const selectedCodes = Array.isArray(value) ? value : [];

    const toggle = (code) => {
        const next = selectedCodes.includes(code)
            ? selectedCodes.filter((v) => v !== code)
            : [...selectedCodes, code];
        onChange?.(next);
    };

    const tags = selectedCodes.map((code) => getLanguageNameByCode(code));

    const normalizedQuery = query.trim().toLowerCase();
    const filteredLanguages = normalizedQuery
        ? SUPPORTED_LANGUAGES.filter(
            (lang) =>
                lang.name.toLowerCase().includes(normalizedQuery)
                || lang.code.toLowerCase().includes(normalizedQuery),
        )
        : SUPPORTED_LANGUAGES;

    const items = filteredLanguages.map((lang) => ({
        label: lang.name,
        checked: selectedCodes.includes(lang.code),
        onClick: () => toggle(lang.code),
    }));

    return (
        <div className="language-selector" style={style}>
            <div className="language-selector-label">
                {label}
                {required && <span className="language-selector-required">*</span>}
                {showTooltip && (
                    <TooltipTrigger text={tooltipText}>
                        <span className="language-selector-help">
                            <SparkIcon slug="circle-info" size="small" />
                        </span>
                    </TooltipTrigger>
                )}
            </div>
            <div
                className={hasError ? 'language-selector-field language-selector-field-error' : 'language-selector-field'}
                onClick={() => setOpen(true)}
                onFocus={() => setOpen(true)}
            >
                <AutoComplete
                    className="spark-select-full-width"
                    type="multi-select"
                    size="medium"
                    placeholder={placeholder}
                    open={open}
                    onOpenChange={handleOpenChange}
                    value={query}
                    tags={tags}
                    items={items}
                    onChange={(e) => setQuery(e.target.value)}
                    onTagRemove={(_tag, index) => toggle(selectedCodes[index])}
                />
            </div>
            {hasError && errorMessage && (
                <div className="language-selector-error">{errorMessage}</div>
            )}
        </div>
    );
};

export default LanguageSelector;
